import type { QueryObserverResult } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import { useBlocker } from "@tanstack/react-router";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { Button } from "../components/ui/button";
import { ApiError } from "../lib/api";
import { cn } from "../lib/utils";
import { BadgeEtatCahier } from "./coloriage-accueil";
import {
  affichageDeLecture,
  ErreurColoriage,
  LIEN_BOUTON,
  LIEN_BOUTON_PLEIN,
  RelectureEchouee,
} from "./coloriage-commun";
import { ALERTE_AMBRE, ALERTE_GRISE, type Retour } from "./coloriage-demandes";
import {
  CLE_COLORIAGE,
  messageDEchec,
  useEcrireSommaire,
  useLancerBrouillon,
  useSommaire,
} from "./coloriage-requetes";
import {
  estimationPC,
  formatLong,
  LIBELLE_NIVEAU,
  lancementEnClair,
  NIVEAUX,
  type Niveau,
  pagesEnClair,
} from "./coloriage-textes";
import {
  type CahierResume,
  CLE_PAGE_MAX,
  clePage,
  type PageSommaire,
  rogner,
  SOUS_TITRE_CAHIER_MAX,
  type Sommaire,
  type SommaireEcrit,
  sujetUtilisable,
  TITRE_CAHIER_MAX,
} from "./contract";
import { compter } from "./format";
import { Chargement } from "./states";

/**
 * Un brouillon (#271, spec du 03/10/2026, section 3) : l'editeur de son sommaire et le
 * panneau « Lancer le rendu ». La page du cahier le pose a la place de la grille tant que le
 * cahier est un brouillon (`coloriage-cahier.tsx`).
 *
 * Le sommaire s'ecrit en entier (decision 2 du plan) : « Enregistrer » envoie toutes les pages
 * avec la version lue, et la console refuse (409 `sommaire_change`) si un autre ecran, ou
 * Claude, a ecrit entre-temps ; « Recharger » remplace alors la saisie, apres confirmation. Les
 * cles des pages se calculent comme sur le PC (`clePage`) et s'affichent sous chaque ligne,
 * elles ne s'ecrivent pas. Le lancement attend que tout soit enregistre, et porte la version que
 * l'ecran montre : si la console en a une autre, c'est le meme 409 et le meme « Recharger »
 * qu'a l'enregistrement. Quitter la page avec une saisie non enregistree demande confirmation
 * (`useBlocker`, et `beforeunload` pour l'onglet). Tout se rogne par `rogner`, comme la console
 * et le PC ; titre et sous-titre tiennent en 60 caracteres, une cle de page en 160.
 *
 * Pas de maquette a ce ticket. Au telephone, les champs s'empilent et les boutons font 44 px.
 */

// ---------------------------------------------------------------------------------------
// La saisie, sans React : ce que l'editeur tient, ce qu'il envoie, ce qu'il refuse.

/** Une ligne de l'editeur : une page telle qu'on la tape, avant tout nettoyage. */
export type LigneSaisie = {
  /** Un numero propre a l'ecran : il suit la ligne quand elle monte ou descend. */
  readonly id: number;
  readonly titre: string;
  readonly sujet: string;
  readonly decor: string;
  /** Vide : comme le cahier. */
  readonly niveau: Niveau | "";
};

/** Ce qu'un enregistrement envoie, sans la version : le sommaire nettoye. */
export type Envoi = Omit<SommaireEcrit, "version">;

/** Ce que l'editeur tient : la saisie, et ce que la console a deja. */
export type Saisie = {
  /** La version du sommaire que le prochain enregistrement remplace. */
  readonly version: number;
  readonly titre: string;
  readonly sousTitre: string;
  readonly lignes: readonly LigneSaisie[];
  /** Le sommaire de la console, nettoye comme un envoi : la saisie se compare a lui. */
  readonly base: Envoi;
  /** Le numero de la prochaine ligne ajoutee. */
  readonly prochaine: number;
};

/** Le nombre de pages d'un sommaire, comme le contrat : 60 au plus. */
export const PAGES_MAX = 60;

function ligneVide(id: number): LigneSaisie {
  return { id, titre: "", sujet: "", decor: "", niveau: "" };
}

/**
 * Vrai pour une ligne ou rien n'est saisi : en fin de liste, elle ne part pas. Rognee comme le
 * fait la console (`rogner`, le `str.strip` de Python) : un blanc que `trim` laisse, U+0085
 * par exemple, ne fait pas une page.
 */
export function estVide(ligne: LigneSaisie): boolean {
  return (
    rogner(ligne.titre) === "" &&
    rogner(ligne.sujet) === "" &&
    rogner(ligne.decor) === "" &&
    ligne.niveau === ""
  );
}

/**
 * Les lignes qui partent : toutes, sauf les lignes vides en fin de liste, celles qu'un
 * sommaire vide ouvre pour chaque dessin voulu. Une ligne vide entre deux pages part, et se
 * fait refuser faute de titre : sinon chaque page qui la suit changerait de rang, donc de cle,
 * a l'enregistrement.
 */
export function lignesQuiPartent(lignes: readonly LigneSaisie[]): readonly LigneSaisie[] {
  let fin = lignes.length;
  while (fin > 0 && estVide(lignes[fin - 1] as LigneSaisie)) fin -= 1;
  return lignes.slice(0, fin);
}

/**
 * Le sommaire tel qu'il part, nettoye comme le schema du contrat le nettoierait : rogne par
 * `rogner` (ce que le serveur fait, et ce que le PC verra), et un decor, un sous-titre ou un
 * niveau vides deviennent null.
 */
export function envoiDe(saisie: Pick<Saisie, "titre" | "sousTitre" | "lignes">): Envoi {
  const sousTitre = rogner(saisie.sousTitre);
  return {
    titre: rogner(saisie.titre),
    sous_titre: sousTitre === "" ? null : sousTitre,
    pages: lignesQuiPartent(saisie.lignes).map((ligne): PageSommaire => {
      const decor = rogner(ligne.decor);
      return {
        titre: rogner(ligne.titre),
        sujet: rogner(ligne.sujet),
        decor: decor === "" ? null : decor,
        niveau: ligne.niveau === "" ? null : ligne.niveau,
      };
    }),
  };
}

/**
 * La saisie d'un sommaire lu. Un sommaire sans page s'ouvre sur autant de lignes vides que de
 * dessins voulus au brief.
 */
export function saisieDe(sommaire: Sommaire): Saisie {
  const lignes: LigneSaisie[] =
    sommaire.pages.length === 0
      ? Array.from(
          { length: Math.min(sommaire.cahier.brief?.dessins ?? 1, PAGES_MAX) },
          (_, rang) => ligneVide(rang + 1),
        )
      : sommaire.pages.map((page, rang) => ({
          id: rang + 1,
          titre: page.titre,
          sujet: page.sujet,
          decor: page.decor ?? "",
          niveau: page.niveau ?? "",
        }));
  const tapee = {
    titre: sommaire.cahier.titre,
    sousTitre: sommaire.cahier.sous_titre ?? "",
    lignes,
  };
  return {
    ...tapee,
    version: sommaire.version,
    base: envoiDe(tapee),
    prochaine: lignes.length + 1,
  };
}

/** Deux envois egaux : construits par `envoiDe`, leurs cles sont toujours dans le meme ordre. */
export function memeEnvoi(a: Envoi, b: Envoi): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** Change une ligne, par son numero. */
export function poserLigne(
  saisie: Saisie,
  id: number,
  morceau: Partial<Omit<LigneSaisie, "id">>,
): Saisie {
  return {
    ...saisie,
    lignes: saisie.lignes.map((ligne) => (ligne.id === id ? { ...ligne, ...morceau } : ligne)),
  };
}

/** Monte (`-1`) ou descend (`1`) une ligne d'un rang ; rien au bord. */
export function deplacer(saisie: Saisie, id: number, sens: -1 | 1): Saisie {
  const lignes = [...saisie.lignes];
  const depart = lignes.findIndex((ligne) => ligne.id === id);
  const arrivee = depart + sens;
  if (depart < 0 || arrivee < 0 || arrivee >= lignes.length) return saisie;
  const [deplacee] = lignes.splice(depart, 1);
  lignes.splice(arrivee, 0, deplacee as LigneSaisie);
  return { ...saisie, lignes };
}

/** Retire une ligne. */
export function retirer(saisie: Saisie, id: number): Saisie {
  return { ...saisie, lignes: saisie.lignes.filter((ligne) => ligne.id !== id) };
}

/** Ajoute une ligne vide en fin de liste ; rien a 60 lignes. */
export function ajouter(saisie: Saisie): Saisie {
  if (saisie.lignes.length >= PAGES_MAX) return saisie;
  return {
    ...saisie,
    lignes: [...saisie.lignes, ligneVide(saisie.prochaine)],
    prochaine: saisie.prochaine + 1,
  };
}

/**
 * Les rangs (de 1 a n) des pages qui partiraient sans titre, ou sans sujet. Un sujet sans
 * lettre ni chiffre (`?`, `...`) compte comme vide : le PC n'en tire aucun prompt, et la
 * console refuse de le lancer (`sujetUtilisable`, la meme regle des deux cotes). Un titre ne
 * manque que vide : `?` donne la cle `01-page`.
 */
export function rangsSans(envoi: Envoi, champ: "titre" | "sujet"): number[] {
  return envoi.pages.flatMap((page, rang) => {
    const manque = champ === "titre" ? page.titre === "" : !sujetUtilisable(page.sujet);
    return manque ? [rang + 1] : [];
  });
}

/** La longueur de la cle d'une page de ce rang et de ce titre, telle que la console la calcule. */
export function longueurDeCle(rang: number, titre: string): number {
  return clePage(rang, rogner(titre)).length;
}

/**
 * Les rangs des pages dont la cle (`clePage(rang, titre)`) depasserait les 160 caracteres d'une
 * cle de page : le contrat les refuse (400), et le PC ne saurait pas nommer leur fichier.
 */
export function rangsCleTropLongue(envoi: Envoi): number[] {
  return envoi.pages.flatMap((page, rang) =>
    longueurDeCle(rang + 1, page.titre) > CLE_PAGE_MAX ? [rang + 1] : [],
  );
}

/**
 * Pourquoi « Enregistrer » refuse : le contrat veut un titre au cahier et a chaque page, un
 * titre et un sous-titre qui tiennent sur la page de titre du PC (60 caracteres), et des cles
 * de page de 160 caracteres au plus.
 */
export function refusDEnregistrement(envoi: Envoi): string[] {
  const refus: string[] = [];
  if (envoi.titre === "") refus.push("Le titre du cahier est vide.");
  if (envoi.titre.length > TITRE_CAHIER_MAX) {
    refus.push(
      `Le titre du cahier fait ${envoi.titre.length} caractères : ${TITRE_CAHIER_MAX} au plus, pour tenir sur la page de titre.`,
    );
  }
  if (envoi.sous_titre !== null && envoi.sous_titre.length > SOUS_TITRE_CAHIER_MAX) {
    refus.push(
      `Le sous-titre fait ${envoi.sous_titre.length} caractères : ${SOUS_TITRE_CAHIER_MAX} au plus, pour tenir sur la page de titre.`,
    );
  }
  if (envoi.pages.length === 0) {
    refus.push("Le sommaire n'a aucune page : donne un titre à une ligne au moins.");
  }
  const sansTitre = rangsSans(envoi, "titre");
  if (sansTitre.length > 0) {
    refus.push(
      `${pagesEnClair(sansTitre, "sans titre")} ${
        sansTitre.length > 1
          ? "Donne-leur un titre, ou retire-les."
          : "Donne-lui un titre, ou retire-la."
      }`,
    );
  }
  const cleTropLongue = rangsCleTropLongue(envoi);
  if (cleTropLongue.length > 0) {
    refus.push(
      `${pagesEnClair(cleTropLongue, `dont la clé dépasse ${CLE_PAGE_MAX} caractères`)} ${
        cleTropLongue.length > 1 ? "Raccourcis leur titre." : "Raccourcis son titre."
      }`,
    );
  }
  return refus;
}

/**
 * Pourquoi « Lancer le rendu » refuse, chaque raison en clair : le sommaire a change ailleurs,
 * la saisie n'est pas enregistree, il est vide, des pages n'ont pas de sujet utilisable (leurs
 * rangs, puis la regle : un sujet `?` ne se voit pas vide a l'ecran). Les pages se lisent sur
 * la saisie : enregistree, c'est aussi ce que la console a.
 */
export function refusDeLancement(
  envoi: Envoi,
  etat: { readonly modifiee: boolean; readonly conflit: boolean },
): string[] {
  const refus: string[] = [];
  if (etat.conflit) {
    refus.push("Le sommaire a changé ailleurs : recharge-le avant de lancer.");
  } else if (etat.modifiee) {
    refus.push(
      "La saisie n'est pas enregistrée : enregistre-la d'abord, le rendu part de ce que la console a.",
    );
  }
  if (envoi.pages.length === 0) refus.push("Le sommaire est vide : écris au moins une page.");
  const sansSujet = rangsSans(envoi, "sujet");
  if (sansSujet.length > 0) {
    refus.push(
      `${pagesEnClair(sansSujet, "sans sujet")} Un sujet s'écrit en anglais, avec au moins une lettre.`,
    );
  }
  return refus;
}

/** Le rang d'une ligne tel que le cahier le numerote : `03`. */
function rangCourt(rang: number): string {
  return String(rang).padStart(2, "0");
}

// ---------------------------------------------------------------------------------------
// Les ecrans.

/** Le nombre de versions par page d'un lancement : de 1 a 10, 3 par defaut. */
const PAR_PAGE = Array.from({ length: 10 }, (_, rang) => rang + 1);
const PAR_PAGE_DEFAUT = 3;

const ETIQUETTE = "text-sm font-semibold";
const AIDE = "text-xs text-muted-foreground";
/** Les champs ; 44 px au doigt (`max-md:min-h-11`), comme les boutons. */
const CHAMP =
  "w-full rounded-md border border-input bg-background px-2.5 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 max-md:min-h-11";
/**
 * Le sujet et le decor : un vrai sujet fait 150 a 250 caracteres. Trois lignes au moins, et la
 * zone grandit avec son texte la ou `field-sizing` existe (Chromium) ; ailleurs, les trois
 * lignes de `rows` et la poignee. Ecrite sans le `max-md:min-h-11` de `CHAMP` : place apres
 * `min-h-20` dans la feuille de style, il l'emportait au telephone et ramenait une zone vide a
 * une ligne (mesure du 3 octobre 2026) ; 80 px depassent de toute facon les 44 px au doigt.
 */
const ZONE_DE_TEXTE =
  "w-full rounded-md border border-input bg-background px-2.5 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 min-h-20 [field-sizing:content]";
/** Les petits boutons d'une ligne : 36 px a la souris, 44 au doigt. */
const BOUTON_LIGNE = cn(
  LIEN_BOUTON,
  "min-h-9 px-3 py-1.5 text-xs disabled:pointer-events-none disabled:opacity-40 max-md:min-h-11",
);
const BOUTON_ARIA = "aria-disabled:cursor-not-allowed aria-disabled:opacity-50 max-md:h-11";

/**
 * Le blocage d'une navigation : toujours, tant qu'il est actif (`disabled` dit quand). Une
 * fonction stable : `useBlocker` se reabonne a chaque nouvelle fonction.
 */
const TOUJOURS = (): boolean => true;

/** L'exemple d'une page, en texte d'attente de la premiere ligne seulement. */
const EXEMPLE = {
  titre: "La chèvre gourmande",
  sujet: "a goat nibbling a straw hat",
  decor: "a farmyard with a wooden fence",
} as const;
const AUCUN_EXEMPLE = { titre: undefined, sujet: undefined, decor: undefined } as const;

/**
 * L'en-tete du brouillon, puis son sommaire. L'en-tete vient du cahier deja lu ; le brief
 * arrive avec le sommaire.
 */
export function EditeurDuBrouillon({
  cahier,
  apresLancement,
}: {
  cahier: CahierResume;
  /** Le lancement a abouti (ou la console dit que le cahier est deja lance) : la page le dit. */
  apresLancement: (retour: Retour) => void;
}) {
  const id = String(cahier.id);
  const lecture = useSommaire(id);
  const client = useQueryClient();
  // Jamais l'ecran d'erreur et l'editeur ensemble (`affichageDeLecture`).
  const affichage = affichageDeLecture(lecture);
  const sommaire = affichage.contenu ? lecture.data : undefined;
  const brief = sommaire?.cahier.brief ?? null;
  const sousTitre = [
    cahier.sous_titre,
    formatLong(cahier.format),
    brief === null ? null : LIBELLE_NIVEAU[brief.niveau],
    brief === null ? null : compter(brief.dessins, "dessin voulu", "dessins voulus"),
  ]
    .filter((morceau) => morceau !== null)
    .join(" · ");

  return (
    <>
      <header className="min-w-0">
        <h1 className="flex flex-wrap items-center gap-3 break-words text-2xl font-semibold">
          {cahier.titre}
          <BadgeEtatCahier etat="brouillon" />
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">{sousTitre}</p>
        {brief === null ? null : (
          <p className="mt-1.5 max-w-3xl break-words text-sm">
            <span className="font-semibold">Thème :</span> {brief.theme}
          </p>
        )}
      </header>
      {lecture.isPending ? <Chargement quoi="du sommaire" /> : null}
      {affichage.ecranDErreur ? (
        <ErreurColoriage error={lecture.error} quoi={`le sommaire du cahier ${id}`} />
      ) : null}
      {affichage.bandeau ? <RelectureEchouee error={lecture.error} /> : null}
      {sommaire === undefined ? null : sommaire.cahier.etat === "brouillon" ? (
        <Brouillon apresLancement={apresLancement} relire={lecture.refetch} sommaire={sommaire} />
      ) : (
        // La grille a dit « brouillon », le sommaire relu dit le contraire : il vient d'etre lance.
        <div className={ALERTE_GRISE}>
          <p>
            <strong className="font-semibold">Ce cahier n'est plus un brouillon :</strong> il a été
            lancé, son sommaire ne change plus.
          </p>
          <button
            className={cn(LIEN_BOUTON, "mt-2.5 max-md:min-h-11")}
            onClick={() => void client.invalidateQueries({ queryKey: CLE_COLORIAGE })}
            type="button"
          >
            Voir le cahier
          </button>
        </div>
      )}
    </>
  );
}

type EtatDuConflit = "aucun" | "signale" | "confirmer";

function Brouillon({
  sommaire,
  relire,
  apresLancement,
}: {
  sommaire: Sommaire;
  relire: () => Promise<QueryObserverResult<Sommaire, Error>>;
  apresLancement: (retour: Retour) => void;
}) {
  const client = useQueryClient();
  const id = String(sommaire.cahier.id);
  const ecrire = useEcrireSommaire(id);
  const lancer = useLancerBrouillon(id);
  const [saisie, setSaisie] = useState<Saisie>(() => saisieDe(sommaire));
  const [conflit, setConflit] = useState<EtatDuConflit>("aucun");
  // Le premier « Enregistrer » refuse montre ses raisons ; elles suivent ensuite la saisie.
  const [tente, setTente] = useState(false);
  const [retour, setRetour] = useState<Retour | null>(null);
  // La console dit que le cahier est deja lance : la saisie reste a l'ecran, a copier.
  const [lanceAilleurs, setLanceAilleurs] = useState<string | null>(null);
  const [echecLancement, setEchecLancement] = useState<string | null>(null);
  const [parPage, setParPage] = useState(PAR_PAGE_DEFAUT);
  // L'element a focaliser apres un rendu : une ligne bouge, part ou arrive.
  const [aFocaliser, setAFocaliser] = useState<string | null>(null);
  // Des verrous synchrones : un double clic arrive avant que `isPending` ne soit rendu.
  const envoiEcriture = useRef(false);
  const envoiLancement = useRef(false);
  const prefixe = useId();

  const envoi = envoiDe(saisie);
  const modifiee = !memeEnvoi(envoi, saisie.base);
  const enConflit = conflit !== "aucun";
  const refusEcriture = refusDEnregistrement(envoi);
  const refusDuLancement = refusDeLancement(envoi, { modifiee, conflit: enConflit });
  const brief = sommaire.cahier.brief;

  // Une version plus recente lue (un autre ecran, Claude) remplace une saisie qui n'a rien a
  // perdre, et leve un 409 devenu sans objet : c'est ce que « Recharger » ferait sans detour.
  // Plus recente seulement : juste apres un enregistrement, la lecture en cache est encore
  // celle d'avant, et la poser defairait ce qui vient d'etre ecrit.
  useEffect(() => {
    if (sommaire.version <= saisie.version || modifiee) return;
    setSaisie(saisieDe(sommaire));
    setConflit("aucun");
    setRetour({ ton: "ok", texte: "Le sommaire a changé ailleurs : voici sa dernière version." });
  }, [sommaire, saisie.version, modifiee]);

  // Fermer l'onglet avec une saisie non enregistree : le navigateur demande confirmation.
  useEffect(() => {
    if (!modifiee) return;
    const retenir = (evenement: BeforeUnloadEvent): void => {
      evenement.preventDefault();
    };
    window.addEventListener("beforeunload", retenir);
    return () => window.removeEventListener("beforeunload", retenir);
  }, [modifiee]);

  // Quitter la page par la console (menu, fil d'Ariane, retour arriere) avec une saisie non
  // enregistree : la page demande d'abord (relecture de #271). La fermeture de l'onglet reste
  // a l'ecouteur ci-dessus, d'ou `enableBeforeUnload: false`.
  const blocage = useBlocker({
    shouldBlockFn: TOUJOURS,
    disabled: !modifiee,
    enableBeforeUnload: false,
    withResolver: true,
  });

  // Les boutons qui disparaissent ou s'eteignent rendent le focus a un voisin (voir les gestes).
  useEffect(() => {
    if (aFocaliser === null) return;
    document.getElementById(aFocaliser)?.focus();
    setAFocaliser(null);
  }, [aFocaliser]);

  const cible = (ligne: number, quoi: string): string => `${prefixe}-${ligne}-${quoi}`;

  const enregistrer = (): void => {
    if (envoiEcriture.current || enConflit) return;
    setTente(true);
    if (refusEcriture.length > 0) {
      // Les raisons prennent le focus : lues tout de suite, et amenees a l'ecran.
      setAFocaliser(`${prefixe}-refus`);
      return;
    }
    if (!modifiee) return;
    envoiEcriture.current = true;
    setRetour(null);
    const envoye = envoi;
    ecrire.mutate(
      { version: saisie.version, ...envoye },
      {
        onSuccess: (reponse) => {
          // La console a maintenant exactement ce qui est parti : c'est la nouvelle base.
          setSaisie((avant) => ({ ...avant, version: reponse.version, base: envoye }));
          setTente(false);
          setRetour({ ton: "ok", texte: "Sommaire enregistré." });
        },
        onError: (error) => {
          if (error instanceof ApiError && error.code === "sommaire_change") {
            setConflit("signale");
            setAFocaliser(`${prefixe}-recharger`);
          } else if (error instanceof ApiError && error.code === "pas_un_brouillon") {
            setLanceAilleurs(error.message);
            setAFocaliser(`${prefixe}-voir-le-cahier`);
          } else {
            setRetour({ ton: "echec", texte: messageDEchec(error) });
            setAFocaliser(`${prefixe}-echec`);
          }
        },
        onSettled: () => {
          envoiEcriture.current = false;
        },
      },
    );
  };

  const recharger = async (): Promise<void> => {
    const relu = await relire();
    if (relu.isSuccess) {
      setSaisie(saisieDe(relu.data));
      setConflit("aucun");
      setTente(false);
      setRetour({ ton: "ok", texte: "Sommaire rechargé : voici sa dernière version." });
      setAFocaliser(`${prefixe}-etat`);
    } else {
      setRetour({ ton: "echec", texte: "Le rechargement n'a pas abouti : réessaie." });
      setAFocaliser(`${prefixe}-echec`);
    }
  };

  const lancerLeRendu = (): void => {
    if (envoiLancement.current || refusDuLancement.length > 0) return;
    envoiLancement.current = true;
    setEchecLancement(null);
    lancer.mutate(
      // La version que l'ecran montre : rien n'attend d'etre enregistre (le lancement l'exige),
      // c'est donc aussi celle que la console avait a la derniere lecture ou ecriture.
      { versions_par_page: parPage, version: saisie.version },
      {
        onSuccess: (reponse) => {
          apresLancement({ ton: "ok", texte: lancementEnClair(reponse.demande) });
        },
        onError: (error) => {
          if (error instanceof ApiError && error.code === "sommaire_change") {
            // Un autre ecran, ou Claude, a ecrit depuis : le meme parcours qu'a l'enregistrement.
            // Le panneau refuse tant que le sommaire n'est pas recharge, et le clic suivant ne
            // lance jamais une version que l'ecran n'a pas montree.
            setConflit("signale");
            setAFocaliser(`${prefixe}-recharger`);
          } else if (error instanceof ApiError && error.code === "pas_un_brouillon") {
            // Lance ailleurs : rien n'attend d'etre enregistre ici (le lancement l'exige), la
            // page peut passer a la grille, qui dit pourquoi.
            apresLancement({ ton: "echec", texte: error.message });
            void client.invalidateQueries({ queryKey: CLE_COLORIAGE });
          } else {
            setEchecLancement(messageDEchec(error));
          }
        },
        onSettled: () => {
          envoiLancement.current = false;
        },
      },
    );
  };

  const nombreDeLignes = saisie.lignes.length;

  return (
    <>
      {blocage.status === "blocked" ? (
        <DialogueQuitter quitter={blocage.proceed} rester={blocage.reset} />
      ) : null}
      <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <section
          aria-labelledby={`${prefixe}-sommaire`}
          className="flex min-w-0 flex-col gap-4 rounded-lg border border-border bg-card p-5 text-card-foreground"
        >
          <div className="flex flex-col gap-1">
            <h2 className="text-base font-semibold" id={`${prefixe}-sommaire`}>
              Sommaire
            </h2>
            <p className="text-sm text-muted-foreground">
              Une ligne par dessin : le titre en français, le sujet et le décor en anglais, pour le
              prompt. Les lignes vides en fin de liste ne partent pas.
            </p>
            <p className="text-sm">
              {compter(envoi.pages.length, "page au sommaire", "pages au sommaire")}
              {brief === null
                ? ""
                : `, ${compter(brief.dessins, "dessin voulu", "dessins voulus")} au brief`}
              .
            </p>
          </div>
          <div className="grid items-start gap-4 md:grid-cols-2">
            <ChampDuCahier
              etiquette="Titre du cahier"
              id={`${prefixe}-titre`}
              maximum={TITRE_CAHIER_MAX}
              poser={(titre) => setSaisie((avant) => ({ ...avant, titre }))}
              valeur={saisie.titre}
            />
            <ChampDuCahier
              etiquette="Sous-titre"
              exemple="24 coloriages pour les 6 à 8 ans"
              id={`${prefixe}-sous-titre`}
              maximum={SOUS_TITRE_CAHIER_MAX}
              poser={(sousTitre) => setSaisie((avant) => ({ ...avant, sousTitre }))}
              valeur={saisie.sousTitre}
            />
          </div>

          {nombreDeLignes === 0 ? (
            <p className="text-sm text-muted-foreground">Aucune ligne : ajoute une page.</p>
          ) : (
            <ol className="flex flex-col gap-3">
              {saisie.lignes.map((ligne, index) => (
                <LigneDuSommaire
                  cible={(quoi) => cible(ligne.id, quoi)}
                  descendre={() => {
                    setSaisie((avant) => deplacer(avant, ligne.id, 1));
                    // Arrivee en bas, « Descendre » s'eteint : le focus passe a « Monter ».
                    setAFocaliser(
                      cible(ligne.id, index + 2 === nombreDeLignes ? "monter" : "descendre"),
                    );
                  }}
                  key={ligne.id}
                  ligne={ligne}
                  monter={() => {
                    setSaisie((avant) => deplacer(avant, ligne.id, -1));
                    // Arrivee en haut, « Monter » s'eteint : le focus passe a « Descendre ».
                    setAFocaliser(cible(ligne.id, index === 1 ? "descendre" : "monter"));
                  }}
                  niveauDuCahier={brief?.niveau ?? null}
                  poser={(morceau) => setSaisie((avant) => poserLigne(avant, ligne.id, morceau))}
                  rang={index + 1}
                  retirer={() => {
                    const restantes = saisie.lignes.filter((autre) => autre.id !== ligne.id);
                    const voisine = restantes[index] ?? restantes[index - 1];
                    setSaisie((avant) => retirer(avant, ligne.id));
                    setAFocaliser(
                      voisine === undefined ? `${prefixe}-ajouter` : cible(voisine.id, "retirer"),
                    );
                  }}
                  total={nombreDeLignes}
                />
              ))}
            </ol>
          )}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <button
              className={cn(
                LIEN_BOUTON,
                "disabled:pointer-events-none disabled:opacity-50 max-md:min-h-11",
              )}
              disabled={nombreDeLignes >= PAGES_MAX}
              id={`${prefixe}-ajouter`}
              onClick={() => {
                setAFocaliser(cible(saisie.prochaine, "titre"));
                setSaisie(ajouter);
              }}
              type="button"
            >
              Ajouter une page
            </button>
            {nombreDeLignes >= PAGES_MAX ? (
              <span className={AIDE}>{PAGES_MAX} pages au plus.</span>
            ) : null}
          </div>

          {/* Les refus et les pannes de l'enregistrement, juste au-dessus de sa barre. Chacun prend
            le focus a son arrivee, ce qui l'amene a l'ecran : la barre, elle, ne grandit pas. */}
          {conflit === "aucun" ? null : (
            <Conflit
              confirmer={() => void recharger()}
              demander={() => {
                // Rien a perdre (la saisie est revenue a ce que la console avait) : sans detour.
                if (modifiee) {
                  setConflit("confirmer");
                  setAFocaliser(`${prefixe}-garder`);
                } else {
                  void recharger();
                }
              }}
              etat={conflit}
              garder={() => {
                setConflit("signale");
                setAFocaliser(`${prefixe}-recharger`);
              }}
              modifiee={modifiee}
              prefixe={prefixe}
            />
          )}
          {lanceAilleurs === null ? null : (
            <div className={ALERTE_AMBRE}>
              <p role="alert">{lanceAilleurs} Ta saisie reste à l'écran, le temps de la copier.</p>
              <button
                className={cn(LIEN_BOUTON, "mt-2.5 max-md:min-h-11")}
                id={`${prefixe}-voir-le-cahier`}
                onClick={() => void client.invalidateQueries({ queryKey: CLE_COLORIAGE })}
                type="button"
              >
                Voir le cahier lancé
              </button>
            </div>
          )}
          {tente && refusEcriture.length > 0 ? (
            <ul
              className="flex flex-col gap-1 rounded-sm text-sm text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              id={`${prefixe}-refus`}
              role="alert"
              tabIndex={-1}
            >
              {refusEcriture.map((raison) => (
                <li key={raison}>{raison}</li>
              ))}
            </ul>
          ) : null}
          {retour?.ton === "echec" ? (
            <p
              className="rounded-sm text-sm text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              id={`${prefixe}-echec`}
              role="alert"
              tabIndex={-1}
            >
              {retour.texte}
            </p>
          ) : null}

          {/* La barre d'enregistrement reste au bas de l'ecran tant que le sommaire defile : au
            telephone, un sommaire de 24 pages fait plusieurs ecrans, et elle dit a tout moment
            ce qui n'est pas enregistre. Toujours de la meme hauteur, que `theme.css` (section 9)
            reserve aux defilements du focus : un champ ne passe jamais dessous. */}
          <div className="coloriage-sommaire-barre sticky bottom-0 z-10 -mx-5 -mb-5 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-b-lg border-t border-border bg-card px-5 py-3.5">
            <Button
              aria-disabled={!modifiee || enConflit || ecrire.isPending || undefined}
              className={cn(BOUTON_ARIA, "max-md:w-full")}
              onClick={enregistrer}
            >
              Enregistrer
            </Button>
            <p
              className="rounded-sm text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              id={`${prefixe}-etat`}
              role="status"
              tabIndex={-1}
            >
              {ecrire.isPending
                ? "Enregistrement..."
                : modifiee
                  ? "Modifications non enregistrées."
                  : retour?.ton === "ok"
                    ? retour.texte
                    : saisie.base.pages.length === 0
                      ? "Rien d'enregistré pour l'instant."
                      : "Tout est enregistré."}
            </p>
          </div>
        </section>

        <section
          aria-labelledby={`${prefixe}-lancer`}
          className="flex min-w-0 flex-col gap-3.5 rounded-lg border border-border bg-card p-5 text-card-foreground xl:sticky xl:top-4"
        >
          <h2 className="text-base font-semibold" id={`${prefixe}-lancer`}>
            Lancer le rendu
          </h2>
          <fieldset className="flex min-w-0 flex-col gap-1.5">
            <legend className={cn(ETIQUETTE, "mb-1.5")}>Où rendre</legend>
            {/* Un seul fournisseur a ce ticket : Nano Banana arrive avec #274, a cote de lui. */}
            <label className="inline-flex min-h-10 w-fit cursor-pointer items-center gap-2 rounded-md border border-foreground bg-muted px-3 py-2 text-sm font-medium max-md:min-h-11">
              <input
                className="size-4 accent-foreground"
                defaultChecked
                name={`${prefixe}-fournisseur`}
                type="radio"
                value="comfy"
              />
              PC (klein, 0 €)
            </label>
          </fieldset>
          <div className="flex flex-col gap-1.5">
            <label className={ETIQUETTE} htmlFor={`${prefixe}-par-page`}>
              Versions par page
            </label>
            <select
              className="h-10 w-24 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-md:h-11"
              id={`${prefixe}-par-page`}
              onChange={(evenement) => setParPage(Number(evenement.target.value))}
              value={parPage}
            >
              {PAR_PAGE.map((nombre) => (
                <option key={nombre} value={nombre}>
                  {nombre}
                </option>
              ))}
            </select>
          </div>
          <p className="text-sm">
            {envoi.pages.length === 0
              ? "Aucune page à rendre pour l'instant."
              : `${estimationPC(envoi.pages.length, parPage)}.`}
          </p>
          <p className={AIDE}>
            La demande part vers l'agent du PC, qui la prend dès qu'il est en ligne. Le cahier
            devient ouvert, et son sommaire ne change plus.
          </p>
          <div>
            <Button
              aria-describedby={
                refusDuLancement.length > 0 ? `${prefixe}-refus-lancement` : undefined
              }
              aria-disabled={refusDuLancement.length > 0 || lancer.isPending || undefined}
              className={cn(BOUTON_ARIA, "max-md:w-full")}
              onClick={lancerLeRendu}
            >
              Lancer le rendu
            </Button>
          </div>
          {refusDuLancement.length > 0 ? (
            <div className={ALERTE_GRISE} id={`${prefixe}-refus-lancement`}>
              <p className="font-semibold">Le rendu attend :</p>
              <ul className="mt-1 flex list-disc flex-col gap-1 pl-5">
                {refusDuLancement.map((raison) => (
                  <li key={raison}>{raison}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {echecLancement === null ? null : (
            <p className="text-sm text-destructive" role="alert">
              {echecLancement}
            </p>
          )}
        </section>
      </div>
    </>
  );
}

/**
 * Le 409 `sommaire_change` : la console a une version plus recente, vue a l'enregistrement ou
 * au lancement. « Recharger » la pose a la place de la saisie, apres confirmation si la saisie
 * a quelque chose a perdre.
 */
function Conflit({
  etat,
  modifiee,
  prefixe,
  demander,
  confirmer,
  garder,
}: {
  etat: Exclude<EtatDuConflit, "aucun">;
  /** Vrai si la saisie differe de ce que la console avait : elle n'est pas enregistree. */
  modifiee: boolean;
  prefixe: string;
  /** « Recharger » : demande confirmation, ou recharge tout de suite s'il n'y a rien a perdre. */
  demander: () => void;
  /** « Remplacer ma saisie » : la version de la console prend sa place. */
  confirmer: () => void;
  /** « Garder ma saisie » : la confirmation se ferme, rien ne change. */
  garder: () => void;
}) {
  return (
    <div className={ALERTE_AMBRE}>
      <p role="alert">
        <strong className="font-semibold">
          Le sommaire a changé ailleurs (Claude ou un autre écran).
        </strong>{" "}
        {modifiee
          ? "Ta saisie n'est pas enregistrée : recharge le sommaire pour voir sa dernière version."
          : "Recharge-le pour voir sa dernière version : le rendu ne part que d'un sommaire que tu as sous les yeux."}
      </p>
      {etat === "signale" ? (
        <button
          className={cn(LIEN_BOUTON, "mt-2.5 max-md:min-h-11")}
          id={`${prefixe}-recharger`}
          onClick={demander}
          type="button"
        >
          Recharger
        </button>
      ) : (
        <section aria-labelledby={`${prefixe}-confirmer`} className="mt-2.5">
          <h3 className="font-semibold" id={`${prefixe}-confirmer`}>
            Remplacer ta saisie ?
          </h3>
          <p className="mt-1">
            La dernière version de la console prend la place de ce que tu as tapé ici : ce qui n'est
            pas enregistré sera perdu. Copie d'abord ce que tu veux garder.
          </p>
          <div className="mt-2.5 flex flex-wrap gap-2">
            <button
              className={cn(LIEN_BOUTON, "max-md:min-h-11")}
              onClick={confirmer}
              type="button"
            >
              Remplacer ma saisie
            </button>
            <button
              className={cn(LIEN_BOUTON, "max-md:min-h-11")}
              id={`${prefixe}-garder`}
              onClick={garder}
              type="button"
            >
              Garder ma saisie
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

/**
 * Une ligne du sommaire : son rang et sa cle calculee, ses trois boutons, puis le titre, le
 * niveau, le sujet et le decor. Le groupe porte le nom de la page (« Page 03 ») : au lecteur
 * d'ecran, chaque champ se dit dans sa page.
 */
function LigneDuSommaire({
  ligne,
  rang,
  total,
  niveauDuCahier,
  cible,
  poser,
  monter,
  descendre,
  retirer,
}: {
  ligne: LigneSaisie;
  rang: number;
  total: number;
  niveauDuCahier: Niveau | null;
  /** L'identifiant d'un element de la ligne : le focus y revient apres un deplacement. */
  cible: (quoi: string) => string;
  poser: (morceau: Partial<Omit<LigneSaisie, "id">>) => void;
  monter: () => void;
  descendre: () => void;
  retirer: () => void;
}) {
  const numero = rangCourt(rang);
  const titre = rogner(ligne.titre);
  const longueur = longueurDeCle(rang, ligne.titre);
  // Une cle de plus de 160 caracteres : le contrat refuse la page, le PC ne saurait pas la nommer.
  const cleTropLongue = titre !== "" && longueur > CLE_PAGE_MAX;
  // L'exemple ne se montre qu'en premiere ligne : repete sous chaque page, il se lirait comme
  // un contenu (une chevre sous « Le chou »).
  const exemple = rang === 1 ? EXEMPLE : AUCUN_EXEMPLE;
  return (
    <li>
      <fieldset className="min-w-0">
        <legend className="sr-only">Page {numero}</legend>
        <div className="flex flex-col gap-2.5 rounded-md border border-border bg-background p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="flex min-w-0 flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
              <span aria-hidden="true" className="text-sm font-semibold tabular-nums">
                {numero}
              </span>
              <span className="break-all font-mono text-xs text-muted-foreground">
                {titre === "" ? "clé : suit le titre" : `clé ${clePage(rang, titre)}`}
              </span>
            </p>
            <span className="flex flex-wrap gap-1.5">
              <button
                aria-label={`Monter la page ${numero}`}
                className={BOUTON_LIGNE}
                disabled={rang === 1}
                id={cible("monter")}
                onClick={monter}
                type="button"
              >
                Monter
              </button>
              <button
                aria-label={`Descendre la page ${numero}`}
                className={BOUTON_LIGNE}
                disabled={rang === total}
                id={cible("descendre")}
                onClick={descendre}
                type="button"
              >
                Descendre
              </button>
              <button
                aria-label={`Retirer la page ${numero}`}
                className={BOUTON_LIGNE}
                id={cible("retirer")}
                onClick={retirer}
                type="button"
              >
                Retirer
              </button>
            </span>
          </div>
          {cleTropLongue ? (
            <p className="text-sm text-destructive" id={cible("cle-refus")}>
              Clé de {longueur} caractères : {CLE_PAGE_MAX} au plus. Raccourcis le titre de cette
              page, sinon le sommaire ne s'enregistre pas.
            </p>
          ) : null}
          {/* 16rem : « comme le cahier (6 à 8 ans) » tient en entier dans la liste du niveau. */}
          <div className="grid items-start gap-2.5 md:grid-cols-[minmax(0,1fr)_minmax(0,16rem)]">
            <ChampDeLigne etiquette="Titre" id={cible("titre")}>
              <input
                aria-describedby={cleTropLongue ? cible("cle-refus") : undefined}
                aria-invalid={cleTropLongue || undefined}
                className={CHAMP}
                id={cible("titre")}
                maxLength={200}
                onChange={(evenement) => poser({ titre: evenement.target.value })}
                placeholder={exemple.titre}
                value={ligne.titre}
              />
            </ChampDeLigne>
            <ChampDeLigne etiquette="Niveau" id={cible("niveau")}>
              <select
                className={cn(CHAMP, "h-10 max-md:h-11")}
                id={cible("niveau")}
                onChange={(evenement) => poser({ niveau: evenement.target.value as Niveau | "" })}
                value={ligne.niveau}
              >
                <option value="">
                  {niveauDuCahier === null
                    ? "comme le cahier"
                    : `comme le cahier (${LIBELLE_NIVEAU[niveauDuCahier]})`}
                </option>
                {NIVEAUX.map((niveau) => (
                  <option key={niveau} value={niveau}>
                    {LIBELLE_NIVEAU[niveau]}
                  </option>
                ))}
              </select>
            </ChampDeLigne>
          </div>
          <div className="grid items-start gap-2.5 md:grid-cols-2">
            <ChampDeLigne etiquette="Sujet (en anglais)" id={cible("sujet")}>
              <textarea
                className={ZONE_DE_TEXTE}
                id={cible("sujet")}
                maxLength={1000}
                onChange={(evenement) => poser({ sujet: evenement.target.value })}
                placeholder={exemple.sujet}
                rows={3}
                value={ligne.sujet}
              />
            </ChampDeLigne>
            <ChampDeLigne etiquette="Décor (en anglais)" id={cible("decor")}>
              <textarea
                className={ZONE_DE_TEXTE}
                id={cible("decor")}
                maxLength={1000}
                onChange={(evenement) => poser({ decor: evenement.target.value })}
                placeholder={exemple.decor}
                rows={3}
                value={ligne.decor}
              />
            </ChampDeLigne>
          </div>
        </div>
      </fieldset>
    </li>
  );
}

function ChampDeLigne({
  etiquette,
  id,
  children,
}: {
  etiquette: string;
  id: string;
  children: ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <label className="text-xs font-semibold text-muted-foreground" htmlFor={id}>
        {etiquette}
      </label>
      {children}
    </div>
  );
}

/**
 * Le titre ou le sous-titre du cahier : 60 caracteres au plus, pour tenir sur la page de titre
 * que le PC compose avant tout rendu (`TITRE_CAHIER_MAX`, `SOUS_TITRE_CAHIER_MAX`). Un texte plus
 * long, ecrit avant la borne, se dit sous le champ, et l'enregistrement le refuse.
 */
function ChampDuCahier({
  etiquette,
  id,
  maximum,
  valeur,
  poser,
  exemple,
}: {
  etiquette: string;
  id: string;
  maximum: number;
  valeur: string;
  poser: (valeur: string) => void;
  exemple?: string;
}) {
  const longueur = rogner(valeur).length;
  const tropLong = longueur > maximum;
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <label className={ETIQUETTE} htmlFor={id}>
        {etiquette}
      </label>
      <input
        aria-describedby={`${id}-aide`}
        aria-invalid={tropLong || undefined}
        className={CHAMP}
        id={id}
        maxLength={maximum}
        onChange={(evenement) => poser(evenement.target.value)}
        placeholder={exemple}
        value={valeur}
      />
      <p className={tropLong ? "text-xs text-destructive" : AIDE} id={`${id}-aide`}>
        {tropLong
          ? `${longueur} caractères : ${maximum} au plus, pour tenir sur la page de titre.`
          : `${maximum} caractères au plus, pour tenir sur la page de titre.`}
      </p>
    </div>
  );
}

/**
 * La confirmation avant de quitter un sommaire qui a des modifications non enregistrees
 * (`useBlocker`). Un `<dialog>` modal, comme la cloture d'un cahier (`coloriage-actions.tsx`) :
 * le reste de la page devient inerte, Echap vaut « Rester ». jsdom n'a pas `showModal` : la
 * boite s'y ouvre par son attribut. Le focus part sur « Rester », jamais sur ce qui perd la
 * saisie.
 */
function DialogueQuitter({ quitter, rester }: { quitter: () => void; rester: () => void }) {
  const boite = useRef<HTMLDialogElement>(null);
  const resterIci = useRef<HTMLButtonElement>(null);
  const titreId = useId();

  useEffect(() => {
    const dialogue = boite.current;
    if (dialogue === null) return;
    if (typeof dialogue.showModal === "function") {
      if (!dialogue.open) dialogue.showModal();
    } else {
      dialogue.setAttribute("open", "");
    }
    resterIci.current?.focus();
    return () => {
      if (typeof dialogue.close === "function" && dialogue.open) dialogue.close();
    };
  }, []);

  return (
    <dialog
      aria-describedby={`${titreId}-texte`}
      aria-labelledby={titreId}
      className="m-auto max-h-[calc(100dvh-2rem)] w-[min(30rem,calc(100%-2rem))] overflow-auto rounded-lg border border-border bg-card p-0 text-card-foreground shadow-lg backdrop:bg-background/80"
      onCancel={(evenement) => {
        evenement.preventDefault();
        rester();
      }}
      onKeyDown={(evenement) => {
        if (evenement.key === "Escape") {
          evenement.preventDefault();
          rester();
        }
      }}
      ref={boite}
    >
      <div className="flex flex-col gap-4 p-5">
        <h2 className="text-lg font-semibold" id={titreId}>
          Quitter sans enregistrer ?
        </h2>
        <p className="text-sm" id={`${titreId}-texte`}>
          Le sommaire a des modifications non enregistrées : en quittant cette page, tu les perds.
          Reste pour les enregistrer.
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            className={cn(LIEN_BOUTON_PLEIN, "max-md:min-h-11")}
            onClick={rester}
            ref={resterIci}
            type="button"
          >
            Rester sur le sommaire
          </button>
          <button className={cn(LIEN_BOUTON, "max-md:min-h-11")} onClick={quitter} type="button">
            Quitter sans enregistrer
          </button>
        </div>
      </div>
    </dialog>
  );
}
